import { Permission } from '@rivethealth/data/user-account';

/**
 * Products and tiers as shown in Riveter Feature Access.
 */
export const permissionedProducts = {
  'Patient Pricing': ['Comply', 'Estimate', 'Automate'] as const,
  'Claim Resolution': ['Resolve'] as const,
  'Payer Performance': ['Benchmark', 'Maximize', 'Audit'] as const,
  'Revenue Diagnostics': [
    'Understand',
    'Forecast',
    'Forecast - Trial',
  ] as const,
};
/** 'Patient Pricing', 'Claim Resolution', etc. */
export const productNames = Object.keys(permissionedProducts) as ProductName[];
/**
 * 'Comply', 'Estimate', 'Understand', 'Resolve', etc.
 */
export const permissionedProductTiers =
  Object.values(permissionedProducts).flat();

export type PermissionedProductTierName =
  (typeof permissionedProductTiers)[number];
export type PermissionedProductTier = {
  id: number;
  name: PermissionedProductTierName;
  permissions?: Permission[];
};
export type ProductName = keyof typeof permissionedProducts;
export type PermissionedProduct = {
  id: number;
  name: ProductName;
  tiers: PermissionedProductTier[];
};

/**
 * Mapping from tiers shown in HubSpot to product names. Ordered from lowest
 * to highest tier within each product.
 */
export const hubspotEquivalents = {
  Comply: 'Patient Pricing',
  Estimate: 'Patient Pricing',
  Automate: 'Patient Pricing',
  Resolve: 'Claim Resolution',
  Benchmark: 'Payer Performance',
  Maximize: 'Payer Performance',
  Audit: 'Payer Performance',
  Understand: 'Revenue Diagnostics',
  Forecast: 'Revenue Diagnostics',
} as const; // satisfies { [tierName: string]: ProductName };
export type HubspotProductTierName = keyof typeof hubspotEquivalents;
/**
 * 'Patient Pricing Comply', 'Revenue Diagnostics Understand', etc.
 */
export const hubspotPrettyTiers = Object.entries(hubspotEquivalents).map(
  ([tier, product]) => `${product} ${tier}`,
) as HubspotPrettyTier[];
export type HubspotPrettyTier = {
  [K in HubspotProductTierName]: `${(typeof hubspotEquivalents)[K]} ${K}`;
}[HubspotProductTierName];

export function hubspotHighestTier(tiers: (HubspotProductTierName | null)[]) {
  for (const highestTier of Object.keys(hubspotEquivalents).reverse()) {
    if (tiers.includes(<any>highestTier)) return highestTier;
  }
  return null;
}
